import { Switch } from "@/components/ui/switch"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Typography } from "@/components/ui/typography"
import { cn } from "@/lib/utils"
import { createContext, useContext, type ReactNode } from "react"

/** A control column on the right of every row, e.g. "In-app" or "Active". */
export interface SettingsListColumn {
  label: string
  /** The column's width and alignment, e.g. "w-20 justify-center". */
  className: string
}

const ColumnsContext = createContext<SettingsListColumn[]>([])

/**
 * One row per item, its controls lined up in columns on the right: the shared
 * layout of the notification preferences, the regulations and the member list.
 * A titled group opens with a heading band; a faint rule sets rows apart.
 */
export function SettingsList({
  columns = [],
  children,
}: {
  columns?: SettingsListColumn[]
  children: ReactNode
}) {
  return (
    <ColumnsContext.Provider value={columns}>
      <div className="flex flex-col">
        {columns.length > 0 && (
          // Every control carries its own label; the header is for the eye.
          <div aria-hidden className="flex justify-end gap-4 px-3 pb-2">
            {columns.map((column) => (
              <div
                key={column.label}
                className={cn("flex shrink-0", column.className)}
              >
                <Typography as="muted">{column.label}</Typography>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-col">{children}</div>
      </div>
    </ColumnsContext.Provider>
  )
}

export function SettingsListGroup({
  title,
  children,
}: {
  /** Shown above the group's rows, e.g. a region. */
  title?: string
  children: ReactNode
}) {
  return (
    <div
      role="group"
      aria-label={title}
      className="flex flex-col not-first:mt-4"
    >
      {title && (
        // A band, so the title reads as the heading of the rows below it.
        <div className="rounded-md bg-accent px-3 py-2.5">
          <Typography as="small">{title}</Typography>
        </div>
      )}
      {children}
    </div>
  )
}

interface SettingsListRowProps {
  label: string
  /** Left of the label, e.g. an avatar. */
  leading?: ReactNode
  /** Next to the label. */
  badges?: ReactNode
  /** Lines under the label. */
  description?: ReactNode
  /** Indented under the row above, which controls it. */
  nested?: boolean
  /** Shown but not available. */
  dimmed?: boolean
  /** One per column of the list, in column order. */
  cells?: ReactNode[]
  /** After the columns, e.g. a role badge in a list without columns. */
  trailing?: ReactNode
  /** Makes the whole row a button, e.g. to open the item's details. */
  onClick?: () => void
}

export function SettingsListRow({
  label,
  leading,
  badges,
  description,
  nested = false,
  dimmed = false,
  cells = [],
  trailing,
  onClick,
}: SettingsListRowProps) {
  const columns = useContext(ColumnsContext)
  // One height for every single-line row, with or without a select in it,
  // so the columns line up from row to row.
  const className = cn(
    "flex min-h-14 flex-wrap items-center gap-x-4 gap-y-2 border-b border-border/50 px-3 py-2.5 last:border-b-0 hover:bg-accent/50 sm:flex-nowrap",
    nested && "pl-9",
  )
  const content = (
    <>
      {leading}
      {/* On a narrow screen the controls drop below the label, still lined
          up under the column headers on the right. */}
      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col gap-1",
          columns.length > 0 && "basis-full sm:basis-0",
        )}
      >
        <div className="flex flex-wrap items-center gap-2">
          {/* `small` is leading-none, which overlaps a label that wraps. A
              row that isn't available reads as muted text. */}
          <Typography as={dimmed ? "muted" : "small"} className="leading-snug">
            {label}
          </Typography>
          {badges}
        </div>
        {description}
      </div>
      {columns.length > 0 && (
        <div className="ml-auto flex shrink-0 gap-4">
          {columns.map((column, index) => (
            <div
              key={column.label}
              className={cn("flex items-center", column.className)}
            >
              {cells[index]}
            </div>
          ))}
        </div>
      )}
      {trailing}
    </>
  )
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        className,
        "w-full text-left focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden",
      )}
    >
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  )
}

/**
 * A row's switch. When it is disabled for a reason worth explaining, the
 * reason shows on hover and focus: a disabled Radix Switch is a native
 * disabled button that takes neither, so a focusable span carries the tooltip.
 */
export function SettingsSwitch({
  checked,
  disabled = false,
  label,
  tooltip,
  onCheckedChange,
}: {
  checked: boolean
  disabled?: boolean
  /** Accessible name, e.g. "Declaration ready for review — in-app". */
  label: string
  tooltip?: string | null
  onCheckedChange: (checked: boolean) => void
}) {
  const control = (
    <Switch
      checked={checked}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
      aria-label={label}
    />
  )
  if (!tooltip) return control
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={disabled ? 0 : undefined}
            className="inline-flex rounded-full focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden"
          >
            {control}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-64">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
